import type React from "react";
import { useEffect, useRef, useState } from "react";
import "react-color-palette/css";
import { useTranslation } from "react-i18next";
import { customIcons } from "../../../assets/icons/customIcons/customIcons";
import { cn } from "../../../util/classNameConstructor";
import Input from "../../atoms/inputs/Input";
import Button from "../button/Button";
import Dropdown from "../dropdown/Dropdown";
import Icon from "../icon/Icon";

const IconPickerContent = ({
  initialIcon,
  handleSaveIcon,
}: {
  handleSaveIcon: (icon: string, opacity: number) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [displayLimit, setDisplayLimit] = useState(50);
  const [selectedIcon, setSelectedIcon] = useState<string | null>(
    initialIcon || null,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredIcons = customIcons
    .filter(
      (icon) =>
        icon.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        icon.keywords.some((keyword) =>
          keyword.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
    )
    .slice(0, displayLimit);

  const handleScroll = () => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 20) {
        setDisplayLimit((prevLimit) => prevLimit + 50);
      }
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, []);

  return (
    <div className="bg-surface-subdued items-between flex w-72 flex-col justify-between">
      <Input
        type="text"
        className="px-3 pt-3"
        placeholder={t("util:search", { val: t("global:icon") })}
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setDisplayLimit(50);
        }}
      />
      <div
        ref={containerRef}
        className="grid h-64 grid-cols-6 content-start gap-2 overflow-y-auto p-3"
      >
        {filteredIcons.map((icon) => (
          <Button
            key={icon.key}
            className={cn(
              "flex w-full items-center justify-center bg-white p-2",
              selectedIcon === icon.key
                ? "border-border-highlight-default bg-surface-pressed border"
                : "",
            )}
            variant="outline"
            pillShaped={false}
            onClick={() => setSelectedIcon(icon.key)}
          >
            {/* {icon.name} */}

            <span className="font-[custom-icons-2]">{icon.key}</span>
          </Button>
        ))}
      </div>

      <div className="flex w-full justify-between border-t p-3">
        {initialIcon && (
          <Button
            variant="shapeless"
            onClick={() => {
              handleSaveIcon(null, 1);
            }}
            text={t("global:reset")}
          />
        )}
        <Button
          variant="primary"
          id="iconPickerSubmit"
          leadingIcon="checkmark-md"
          onClick={() => handleSaveIcon(selectedIcon, 1)}
          disabled={!selectedIcon}
          classNameWrapper="ml-auto"
        />
      </div>
    </div>
  );
};

interface IconPickerProps {
  initialIcon: string;
  initialOpacity?: number;
  handleSave: (icon: string, opacity: number) => Promise<void>;
}

const IconPicker: React.FC<IconPickerProps> = ({
  initialIcon,
  initialOpacity = 1,
  initialColor = "#000",
  handleSave,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const initialIconRef = useRef(initialIcon);
  const handleSaveIcon = async (icon: string, opacity: number) => {
    try {
      console.log(icon);
      await handleSave(icon, opacity);
      setDropdownOpen(false);
    } catch (error) {
      console.error(error);
    }
  };

  const initialIconObject = customIcons.find(
    (icon) => icon.key === initialIcon,
  );

  return (
    <Dropdown
      size="sm"
      open={dropdownOpen}
      className="bg-transparent"
      setOpen={(val) => {
        if (val === false) {
          setTimeout(() => {
            // Reset to initial icon if needed
          }, 100);
        }

        setDropdownOpen(val);
      }}
      trailingIcon={
        <span className="font-[custom-icons-2]">
          {initialIconObject ? (
            initialIconObject.key
          ) : (
            <div className=" bg-text-disabled flex h-4 w-4 items-center justify-center rounded-[3px]">
              <Icon name="plus-md" className="text-white drop-shadow-sm" />
            </div>
          )}
        </span>
      }
      items={[
        {
          children: (
            <div className="flex">
              <IconPickerContent
                handleSaveIcon={handleSaveIcon}
                initialIcon={initialIcon}
              />
            </div>
          ),
        },
      ]}
    />
  );
};

export default IconPicker;
